import React, { useState, useRef, useEffect } from "react";
import "./TimePicker.css";
import { TbClockHour5 } from "react-icons/tb";
import { useLanguage } from "../../LanguageProvider";
import { useToaster } from "../../ToasterProvider";

const TimePicker = ({
  onTimeSelect,
  toggleLabel,
  style,
  buttonStyle,
  align = "left",
  initialTime = null,
  quarterIntervals = false,
  dropdownPosition = "bottom", // Yeni prop: "bottom" veya "top"
}) => {
  const { toastWarning } = useToaster();
  const { getTranslation } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedHour, setSelectedHour] = useState(null);
  const [selectedMinute, setSelectedMinute] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState("AM");

  const [tempSelectedHour, setTempSelectedHour] = useState(null);
  const [tempSelectedMinute, setTempSelectedMinute] = useState(null);
  const [tempSelectedPeriod, setTempSelectedPeriod] = useState("AM");

  const timePickerRef = useRef(null);

  const hours = Array.from({ length: 12 }, (_, i) =>
    (i + 1).toString().padStart(2, "0")
  );
  const minutes = quarterIntervals
    ? ["00", "15", "30", "45"]
    : Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, "0"));

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        timePickerRef.current &&
        !timePickerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        resetTempValues();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (initialTime) {
      const [hour, minute] = initialTime.split(":").map(Number);
      let period = "AM";
      let formattedHour = hour;

      if (hour === 0) {
        formattedHour = 12; // 00:00 → 12:00 AM
      } else if (hour === 12) {
        period = "PM"; // 12:00 → 12:00 PM
      } else if (hour > 12) {
        formattedHour = hour - 12; // 13:00 → 01:00 PM
        period = "PM";
      }

      const hourString = formattedHour.toString().padStart(2, "0");
      const minuteString = minute.toString().padStart(2, "0");

      if (hours.includes(hourString) && minutes.includes(minuteString)) {
        setSelectedHour(hourString);
        setSelectedMinute(minuteString);
        setSelectedPeriod(period);
      }
    }
  }, [initialTime]);

  const applyTime = (hour, minute, period) => {
    setSelectedHour(hour);
    setSelectedMinute(minute);
    setSelectedPeriod(period);
    onTimeSelect(`${hour}:${minute} ${period}`);
    setIsOpen(false);
  };

  const handleApply = () => {
    if (!tempSelectedHour || !tempSelectedMinute) {
      toastWarning(getTranslation("pleaseSelectValidTime"));
      return;
    }
    applyTime(tempSelectedHour, tempSelectedMinute, tempSelectedPeriod);
  };

  const handleCancel = () => {
    resetTempValues();
    setIsOpen(false);
  };

  const resetTempValues = () => {
    setTempSelectedHour(selectedHour);
    setTempSelectedMinute(selectedMinute);
    setTempSelectedPeriod(selectedPeriod);
  };

  return (
    <div ref={timePickerRef} style={style} className="timepicker-container">
      <button
        style={buttonStyle}
        className="timepicker-toggle-btn"
        onClick={() => {
          setIsOpen(!isOpen);
          resetTempValues();
        }}
      >
        {selectedHour && selectedMinute ? (
          `${selectedHour}:${selectedMinute} ${selectedPeriod}`
        ) : (
          <span
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              display: "flex",
              paddingInline: "5px",
            }}
          >
            {toggleLabel} <TbClockHour5 size={20} />
          </span>
        )}
      </button>
      {isOpen && (
        <div
          className={`timepicker-dropdown ${
            dropdownPosition === "top" ? "dropdown-top" : ""
          } ${align === "right" ? "align-right" : "align-left"}`}
        >
          <div className="timepicker-grid">
            <div className="time-column">
              <div className="scrollable">
                {hours.map((hour) => (
                  <div
                    key={hour}
                    className={`time-cell ${
                      tempSelectedHour === hour ? "selected" : ""
                    }`}
                    onClick={() => setTempSelectedHour(hour)}
                    onDoubleClick={() => {
                      const newMinute = tempSelectedMinute || "00";
                      applyTime(hour, newMinute, tempSelectedPeriod);
                    }}
                  >
                    {hour}
                  </div>
                ))}
              </div>
            </div>
            <div className="time-column">
              <div className="scrollable">
                {minutes.map((minute) => (
                  <div
                    key={minute}
                    className={`time-cell ${
                      tempSelectedMinute === minute ? "selected" : ""
                    }`}
                    onClick={() => setTempSelectedMinute(minute)}
                    onDoubleClick={() => {
                      if (tempSelectedHour) {
                        applyTime(tempSelectedHour, minute, tempSelectedPeriod);
                      }
                    }}
                  >
                    {minute}
                  </div>
                ))}
              </div>
            </div>
            <div className="time-column">
              {["AM", "PM"].map((period) => (
                <div
                  key={period}
                  className={`time-cell ${
                    tempSelectedPeriod === period ? "selected" : ""
                  }`}
                  onClick={() => setTempSelectedPeriod(period)}
                >
                  {period}
                </div>
              ))}
            </div>
          </div>
          <div className="timepicker-footer">
            <button className="cancel" onClick={handleCancel}>
              {getTranslation("cancel")}
            </button>
            <button
              className={`apply ${
                tempSelectedHour && tempSelectedMinute ? "" : "disabled"
              }`}
              onClick={handleApply}
              disabled={!tempSelectedHour || !tempSelectedMinute}
            >
              {getTranslation("apply")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimePicker;
